<script setup>
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import { DsButton, DsAlert, DsText } from '@coloplast/design-system';
import notification from 'shared/components/basket/notification.vue';
import FlyOutBasketTotal from 'shared/components/basket/flyOutBasketTotal.vue';
import { track_order_summary } from 'shared/services/tracking/checkoutTrackingService';
import { FormKit } from '@formkit/vue';

const props = defineProps({
	labels: Object,
	submitModel: Object,
	basket: Object,
	quoteModel: Object,
	continueText: String,
	nextLink: String,
});

const store = useStore();
const showSpinner = () => store.dispatch('showSpinner');
const e1TaxSettings = computed(() => store.getters.getE1TaxSettings);
const e1TaxCalculate = computed(() => store.getters.getE1TaxCalculate);
const isConsumerPrescriptionsEnabled = computed(() => store.getters.getGlobalSettings.IsConsumerPrescriptionsEnabled);

const consentAccepted = ref(false);

function handleContinueClick() {
	showSpinner();

	track_order_summary(props.basket);

	window.location.assign(props.nextLink);
}

const numberOfItems = computed(() => {
	const allItems = props.basket.paid || [];
	const paid = allItems.filter((item) => !item.isComplimentary);
	return paid.length;
});

const anyComplimentaryItems = computed(() => props.basket?.complimentary.length);

const hasOnlySamplesLineItems = computed(() => {
	return props.basket && props.basket?.samples?.length > 0 && props.basket?.paid?.length == 0;
})

const isShowE1Message = computed(() => {
	return Boolean(
		e1TaxCalculate.value.taxParams?.showE1FixedTaxRateNotification
		&& !hasOnlySamplesLineItems.value
		&& numberOfItems.value > 0
	);
});

const showConsent = computed(() => {
	return Boolean(
		props.submitModel?.termsAndCondition?.data
	);
});

const requireConsent = computed(() => {
	return Boolean(
		props.submitModel?.termsAndCondition?.data &&
		!props.submitModel.termsAndCondition.data.isOptional
	);
});
</script>
<template>
	<div>
		<FlyOutBasketTotal
			:labels="labels"
			:show-total="!isConsumerPrescriptionsEnabled"
			:hideTotalForReimbursableOnly="labels.hideTotal"
		/>

		<DsAlert
			v-if="submitModel.reimbursementInformationViewModel && submitModel.reimbursementInformationViewModel.displayMessageOfPayment"
			class="exempt-status-alert-message"
			appearance="highlight"
			:text="submitModel.reimbursementInformationViewModel.messageOfPayment"
		/>

		<notification
			v-if="isShowE1Message"
			iconName="information-circle"
			:message="e1TaxSettings.E1FixedTaxRateInformationalMessage"
			data-testid="e1Message"
		/>

		<template v-if="e1TaxSettings.IsE1TaxCalculationEnabled">
			<notification
				v-if="numberOfItems > 0 && e1TaxCalculate.taxParams && e1TaxCalculate.taxParams.isPostCodeUnknown !== null"
				:message="e1TaxCalculate.taxParams.isPostCodeUnknown ? labels.notAdjustedVatMessage : labels.adjustedVatMessage"
				:iconName="!e1TaxCalculate.taxParams.isPostCodeUnknown ? 'checkmark' : 'information-circle'"
				data-testid="vatMessage" />
		</template>
		<template v-else>
			<notification
				:message="basket?.isTaxValidated ? labels.adjustedVatMessage : labels.notAdjustedVatMessage"
				:iconName="!basket.isTaxValidated ? 'checkmark' : 'information-circle'"
				v-if="basket.marketSpecificTax && numberOfItems > 0"
				data-testid="vatMessage" />
		</template>


		<notification
			:message="basket.hiddenComplimentaryItemsQuantityMessage"
			v-if="basket.hideComplimentaryItemsQuantity && anyComplimentaryItems"
			data-testid="hideComplimentaryItemsQuantity"
			iconName="information-circle"
		/>

		<div v-if="showConsent" class="checkout-summary-consent">
			<FormKit
				type="checkbox"
				v-model="consentAccepted"
				:label="submitModel?.termsAndCondition?.data?.text"
				:validation="requireConsent ? 'accepted' : null"
				:validation-messages="{
					required: submitModel?.termsAndCondition?.data?.errorMessage,
					accepted: submitModel?.termsAndCondition?.data?.errorMessage,
				}"
				data-testid="consentAcceptedCheckbox"
			>
				<template #label>
					<div>
						<DsText html-element="span" text-style="regular" text-type="label-md">{{ submitModel?.termsAndCondition?.data?.text }}</DsText>
						<DsButton
							v-if="submitModel?.termsAndCondition?.data?.url"
							variant="link"
							size="md"
							class="checkout-summary-consent-link"
							:href="submitModel?.termsAndCondition?.data?.url"
						>
							{{ submitModel?.termsAndCondition?.data?.title }}
						</DsButton>
					</div>
				</template>
			</FormKit>
		</div>

		<div v-if="continueText && nextLink">
			<DsButton
				class="checkout-summary-continue-button"
				:clickHandler="handleContinueClick"
				variant="primary"
				:disabled="e1TaxCalculate.isE1TaxLoading || (!consentAccepted && requireConsent)"
			>
				{{ continueText }}
			</DsButton>
		</div>

		<template v-if="quoteModel != null && quoteModel.BaseViewModel.IsVisible">
			<p></p>
			<div v-html="quoteModel.BaseViewModel.BodyText"></div>

			<DsButton
				class="checkout-summary-continue-button"
				:href="quoteModel.BaseViewModel.QuotePageLink"
				variant="secondary"
			>
				{{ quoteModel.BaseViewModel.ButtonLabel }}
			</DsButton>
		</template>
	</div>
</template>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.exempt-status-alert-message {
	margin-block-end: ds-space(8);

	@include t-respond-to-min('medium') {
		margin-block-end: ds-space(11);
	}
}

.checkout-summary-continue-button {
	width: 100%;
	justify-content: center;

	@at-root {
		a#{&} {
			margin-top: ds-space(11);
			@include t-respond-to-max('xlarge') {
				margin-top: ds-space(8);
			}
		}
		button#{&} {
			margin: ds-space(11) 0 ds-space(11) 0;
			@include t-respond-to-max('xlarge') {
				margin: ds-space(8) 0 ds-space(8) 0;
			}
		}
	}

	@include t-respond-to-min('medium') {
		width: auto;
	}
}

.checkout-summary-consent {
	margin-top: ds-space(8);

	.formkit-wrapper {
		max-width: inherit;
		align-items: flex-start;
	}

	&-link {
		display: inline;
	}
}

</style>
